<template>
  <div class="relative inline-flex items-center">
    <UserAvatarMenuButton
      :show-name="showName"
      data-testid="button-user-menu"
      @click="toggle"
    />

    <Popover ref="popoverRef" dismissable aria-label="User menu" class="user-menu-popover">
      <div class="w-64">
        <div class="flex items-center gap-3 px-1 py-3 border-b border-gray-100">
          <Avatar :user="user || undefined" size="sm" :ring="false" />
          <div class="flex flex-col min-w-0">
            <span class="font-semibold text-sm truncate">{{ displayName }}</span>
            <span v-if="user?.email" class="text-xs text-gray-500 truncate">{{ user.email }}</span>
          </div>
        </div>

        <Can do="admin_dashboard.access">
          <div class="px-1 py-3 border-b border-gray-100">
            <Button
              as="router-link"
              to="/admin"
              class="p-button-primary w-full"
              data-testid="button-open-admin-dashboard"
              @click="hide"
            >
              <span class="flex items-center gap-2 w-full">
                <i class="fa-solid fa-shield-halved" aria-hidden="true"></i>
                <span class="flex-1 text-left">Open Admin Dashboard</span>
              </span>
            </Button>
          </div>
        </Can>

        <ul class="list-none p-0 m-0">
          <li>
            <RouterLink
              :to="{ name: 'my-profile' }"
              class="flex items-center gap-3 px-4 py-3 text-sm text-primary hover:bg-gray-50"
              data-testid="link-my-profile"
              @click="hide"
            >
              <i class="fa-solid fa-user text-gray-500" aria-hidden="true"></i>
              <span>My Profile</span>
            </RouterLink>
          </li>
          <li>
            <button
              type="button"
              class="w-full flex items-center gap-3 px-4 py-3 text-sm text-primary hover:bg-gray-50 bg-transparent border-none cursor-pointer text-left"
              data-testid="button-sign-out"
              @click="signOut"
            >
              <i class="fa-solid fa-right-from-bracket text-gray-500" aria-hidden="true"></i>
              <span>Sign out</span>
            </button>
          </li>
        </ul>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import Avatar from '@/components/common/Avatar.vue'
import UserAvatarMenuButton from '@/components/common/UserAvatarMenuButton.vue'
import Can from '@/components/auth/Can.vue'

import { useUserStore } from '@/stores/user'
import AuthService from '@services/auth/authService'

defineProps({
  showName: {
    type: Boolean,
    default: true,
  },
})

const userStore = useUserStore()
const route = useRoute()

const user = computed(() => userStore.getUser)

const displayName = computed(() => {
  const u = user.value
  if (!u) return ''
  const composed = `${u.first_name || ''} ${u.last_name || ''}`.trim()
  return u.display_name || composed || u.username || ''
})

const popoverRef = ref(null)

const toggle = (event) => {
  popoverRef.value?.toggle(event)
}

const hide = () => {
  popoverRef.value?.hide()
}

const signOut = () => {
  hide()
  AuthService.logout()
}

watch(() => route.fullPath, hide)
</script>

<style scoped>
:deep(.user-menu-popover .p-popover-content) {
  padding: 0;
}
</style>
